import { useContext,useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Tooltip, Zoom } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ClaimType } from "../../../constants/claims";
import { ClaimsContext } from "../../../services/ClaimsService";
import "./CompanyEmissionReportDetails.scss";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BreadCrumb from "../../../common/breadCrumb/BreadCrumb";
import CompanyEmissionReportPage from "./CompanyEmissionReportPage";
import VerificationPage from "./VerificationPage";
import { reportsActions } from "../../../store/reports/reports.slice";
import { Tab, Button, Icon,Modal,COLORS } from "abs-ui-components";
import { debounce } from "lodash";
import axiosHttp from "../../../services/interceptor";
import moment from "moment";
import { deleteReport } from "../CERHelpers";
import { emitToasterMsg } from "../../../services/toasterUtility";

const CompanyEmissionReportDetails = ({ defaultActiveTab = 0 }) => {
	const { userHasClaim , userHasFeature } = useContext(ClaimsContext);
	const navigate = useNavigate();
	const { companyVerificationReportId } = useParams();
	const [companyParticulars, setCompanyParticulars] = useState([]);
	const [shipERs, setShipERs] = useState([]);
	const [annualEmissions, setAnnualEmissions] = useState([]);
	const [reportData, setReportData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const baseUrl = process.env.REACT_APP_BASE_URL;
    const verificationStatuses = useSelector(
			(state) => state.reports.verificationStatus
		);
    const dispatch = useDispatch();
	const getCompanyEmissionReport = async () => {
		try {
			setLoading(true);


			const response = await axiosHttp.get(
				`${baseUrl}api/CompanyVerification/GetCompanyEmissionReportById?companyVerificationReportId=${companyVerificationReportId}`
			);
			const data = response?.data?.data;

			//reportData
			setReportData(data);
			// Company Particulars
			setCompanyParticulars([
				{
					companyImoNumber: data.companyImoNumber,
					xmlCompanyName: data.xmlCompanyName,
				},
			]);

			// Ship ERs
			setShipERs(
				(data.shipErs || []).map((ship) => ({
					shipImo: ship.shipImo,
					name: ship.name,
					flag: ship.flag,
					shipType: ship.shipType,
					grossTonnage: ship.grossTonnage,
					iceClass: ship.iceClass,
					reportingPeriod: ship.reportingPeriod,
					partialPeriodFromDate: ship.partialPeriodFromDate,
                    partialPeriodToDate: ship.partialPeriodToDate,
					shipownerImoNumber: ship.shipOwnerImoNumber,
					co2eqEmissions: ship.co2EqEmissions,
					erStatus: ship.erStatus,
				}))
			);
			const summary = data.annualEmissionSummary;
			const annualEmissionRows = Object.keys(summary).map((key) => ({
				aggregationDescription: key.replace("point", "Point "),
				co2: summary[key].co2Tonnes,

				ch4Tonnes: summary[key].ch4Tonnes,
				ch4Co2EqTonnes: summary[key].ch4Co2EqTonnes,

				n2OTonnes: summary[key].n2OTonnes,
				n2OCo2EqTonnes: summary[key].n2OCo2EqTonnes,

				totalGhgEmissions: summary[key].totalGhgCo2EqTonnes,
			}));

			setAnnualEmissions(annualEmissionRows);
		} catch (error) {
			console.error("Error fetching Company Emission Report:", error);
		} finally {
			setLoading(false);
		}
	};
	if (verificationStatuses) {
			getCompanyEmissionReport();
			dispatch(reportsActions.setVerificationStatus(false));
		}
    const formatDate = (dateString) => {
			return moment(dateString).format("DD-MMM-YYYY");
		};

	const formattedDate = reportData?.dateOfIssuance
		? formatDate(reportData?.dateOfIssuance)
		: "";

	const tooltipTitle =
			reportData?.status === "Verification Completed" ? (
				<>
					Reviewer : <span className="font-style">{reportData?.latestReviewer}</span>
					<br />
					Verifier : <span className="font-style">{reportData?.latestVerifier}</span>
					<br />
					Verification Complete Date : {formattedDate}
				</>
			) :(
				""
			);

	useEffect(() => {
		getCompanyEmissionReport();
	}, []);

	useEffect(() => {
		if (companyVerificationReportId) {
			getCompanyEmissionReport();
		}
	}, [companyVerificationReportId]);

	const reportDetailsData = {
		reportId: reportData?.cvrId,
		xmlCompanyName: reportData?.xmlCompanyName,
		companyImo: reportData?.companyImoNumber,
		reportStatus: reportData?.status,
	};

	const icon = (() => {
		switch (reportDetailsData?.reportStatus) {
			case "Verification Completed":
				return <Icon icon="check" />;
			case "Verification In Progress":
				return (
					<HourglassEmptyIcon
						sx={{ fontSize: 18, marginTop: "-1px" }}
					/>
				);
			case "Crosscheck In Progress":
				return (
					<HourglassEmptyIcon
						sx={{ fontSize: 18, marginTop: "-1px" }}
					/>
				);
			default:
				return null;
		}
	})();

	const [showbreadCrumb, setShowBreadCrumb] = useState(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		return storedValue !== null ? JSON.parse(storedValue) : true;
	});

	// Create a debounced setter function
	const debouncedSetHeaderFooterEnabled = debounce((value) => {
		setShowBreadCrumb(value);
	}, 100);

	const tabData = [
		{
			header: "Company Emission Report",
		},
		{
			header: "Verification",
		},
	];

	// Use the debounced function
	useEffect(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		if (storedValue !== null) {
			debouncedSetHeaderFooterEnabled(JSON.parse(storedValue));
		}
	}, [debouncedSetHeaderFooterEnabled]);

	const capitalizeFirstLetter = (stringg) => {
		return stringg
			? stringg
				.toLowerCase()
				.split(" ")
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(" ")
			: "";
	};

	const [tab, setTab] = useState(
		capitalizeFirstLetter(
			tabData[Number(defaultActiveTab)].header.toLowerCase()
		)
	);

	const [activeTab, setActiveTab] = useState(defaultActiveTab);

	const navigationItems= [
		{
			label: "Annual Emission Verification",
			url: "/eumrv-company-emission-verification-report",
			navId: "annual_emission_Verification_navId",
		},
		{
			label: "EU MRV Company Emission Reports",
			url: "/eumrv-company-emission-verification-report",
			navId: "eumrv_company_emission_verification_navId"
		},
		{
			label: `${tab}`,
			url: "",
			navId: `${tab.replace(" ", "_")}_navId`
		},
	];

	const handleTabChange = (tab) => {
		setTab(tab);
		let tempNavItems = navigationItems.splice(0, 3);

		tempNavItems.push({
			label: `${capitalizeFirstLetter(tab)}`,
			url: "",
			navId: `${tab.replace(" ", "_")}_navId`,
		});

	};

	const handleReturnToCompanyVerification = () => {
          navigate("/eumrv-company-emission-verification-report");
    };

	const tabsWithContent = [
		{
			header: "Company Emission Report",
			content: (
				<>
					{activeTab === 0 && (
						<CompanyEmissionReportPage shipERs={shipERs} companyParticulars={companyParticulars} annualEmissions={annualEmissions} isLoading={loading} />
					)}
				</>
			),
		},
		userHasClaim(ClaimType.VIEW_VERIFICATION_TAB)
					? 
		{
			header: "Verification",
			content: (
				<>
					{activeTab === 1 && (
						<VerificationPage companyVerificationReportId={companyVerificationReportId} />
					)}
				</>
			),
		}
		: "",
	];

	const tabChangeHandler = (event) => {
		setActiveTab(event);
		handleTabChange(tabsWithContent[event].header.toLowerCase());
	};

	 if (loading) {
				return (
					<div
							role="status"
							aria-label="Loading cerdata"
							style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
						>
							<Box>
								<CircularProgress size={32} thickness={5} />
							</Box>
						</div>
				);
			}

	const handleDelete = async () => {
		try {
			setIsDeleting(true);
			await deleteReport(companyVerificationReportId);
			emitToasterMsg("success", "CER Report", "Deleted Successfully");
			setConfirmDelete(false);
			navigate("/eumrv-company-emission-verification-report", { replace: true });
		} catch (error) {
			console.error("Error deleting CER:", error);
			emitToasterMsg("error", "CER Report", "Failed to delete the report");
		} finally {
			setIsDeleting(false);
		}
	};
	return (
		<>
			<div className="m-4 details-container">
				<div className="actions-top">{showbreadCrumb && <BreadCrumb items={navigationItems} />}</div>

				{/* Top Bar with Report Info and Actions */}
				<div className="mb-2 d-flex justify-content-between" style={{ marginTop: "1.5rem" }}>
					<div className="report-info">
						<span className="header">{reportDetailsData.reportId}</span>
						<span className="text-gray ml-4 fw-bolder">
							{reportDetailsData.xmlCompanyName}, Company IMO# {reportDetailsData.companyImo}
						</span>
					</div>

					<div className="d-flex">
						<Button variant="tertiary" startIcon={<Icon icon="arrow_back" />} className="mr-4" onClick={handleReturnToCompanyVerification}>
							Return to EU MRV Company Emission Reports
						</Button>
						<Button variant="tertiary" startIcon={<Icon icon="delete" />} className="mr-4" onClick={() => setConfirmDelete(true)}>
							Delete CER
						</Button>
						<div className="confirmation-modal-container"></div>

						{reportDetailsData?.reportStatus && (
							<Tooltip
								title={tooltipTitle}
								placement="top"
								TransitionComponent={Zoom}
								arrow
								componentsProps={{
									tooltip: {
										sx: {
											fontSize: "13px",
											backgroundColor: "#012c51 !important",
											color: "white",
										},
									},
								}}
							>
								<span style={{ display: "inline-flex", alignItems: "center" }}>
									<Button variant="tertiary" startIcon={icon} className="mr-4" disabled={true}>
										{reportDetailsData.reportStatus}
									</Button>
								</span>
							</Tooltip>
						)}
					</div>
				</div>

				<div>
					{tabsWithContent && (
						<Tab
							tabData={tabsWithContent.map((tab) => ({
								...tab,
								header: (
									<span
										style={{
											textTransform: "capitalize",
										}}
									>
										{tab.header}
									</span>
								),
							}))}
							onTabChange={tabChangeHandler}
							activeTab={activeTab}
						></Tab>
					)}
				</div>
            <div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => handleDelete()}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this CER Report</div>
				</Modal>
			</div>
			</div>
		</>
	);
};

export default CompanyEmissionReportDetails;
